import React, { useRef, useState } from 'react';
import { FaTimes, FaFileAlt, FaUpload, FaCheckCircle, FaPaperPlane, FaTrash, FaEye, FaCheckDouble } from 'react-icons/fa';

/**
 * Modal for attaching a file to a program requirement.
 * - Attach from files already in the conversation
 * - Upload a new file from the computer (then attach)
 * - Remove the current attachment
 * - Request the document from the client (sends it into the conversation feed)
 */
const RequirementAttachModal = ({
  item, files = [], threadId, authenticatedAxios,
  onAttachFile, onUnlink, onRequest, onVerify, onViewFile, onClose
}) => {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!item) return null;

  const fulfilled = item.reqStatus === 'fulfilled' || item.reqStatus === 'verified';
  const attached = (item.reqFiles && item.reqFiles[0]) || null;

  const run = async (fn) => {
    setBusy(true); setError('');
    try { await fn(); onClose(); }
    catch (e) { setError(e?.response?.data?.error || e?.message || 'Something went wrong'); }
    finally { setBusy(false); }
  };

  const handleUpload = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;
    setBusy(true); setError('');
    try {
      const form = new FormData();
      form.append('file', raw);
      form.append('threadId', threadId);
      const res = await authenticatedAxios.post('/conversation-files/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploaded = res.data.file || res.data;
      await onAttachFile(uploaded);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Upload failed');
    } finally { setBusy(false); }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 20 };
  const card = { width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', background: '#111827', border: '1px solid rgba(75,85,99,0.4)', borderRadius: 14, padding: 24, color: '#e8eaed' };
  const rowBtn = { display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', marginBottom: 8, background: 'rgba(31,41,55,0.5)', border: '1px solid rgba(75,85,99,0.25)', borderRadius: 8, color: '#e8eaed', cursor: 'pointer', fontSize: 13 };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{item.name}</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>{item.description || (item.category === 'form' ? 'Required form' : 'Required document')}</div>
          </div>
          <FaTimes style={{ cursor: 'pointer', color: '#9ca3af' }} onClick={onClose} />
        </div>

        {attached && (
          <div style={{ margin: '16px 0', padding: '12px', background: item.reqStatus === 'verified' ? 'rgba(16,185,129,0.14)' : 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaCheckCircle style={{ color: '#10b981', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: '#e8eaed', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attached.fileName}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>Attached{item.reqFulfilledBy ? ` by ${item.reqFulfilledBy}` : ''}{item.reqStatus === 'verified' ? ' · ✓ verified' : ''}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
              <button disabled={busy} onClick={() => onViewFile && onViewFile(attached)} style={linkAction('#60a5fa')}>
                <FaEye size={11} /> View
              </button>
              {item.reqStatus !== 'verified' && onVerify && (
                <button disabled={busy} onClick={() => run(onVerify)} style={linkAction('#10b981')}>
                  <FaCheckDouble size={11} /> Mark as correct
                </button>
              )}
              <button disabled={busy} onClick={() => run(onUnlink)} style={linkAction('#ef4444')}>
                <FaTrash size={11} /> Remove
              </button>
            </div>
          </div>
        )}

        <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', letterSpacing: 0.5, margin: '18px 0 8px' }}>{attached ? 'Replace — upload a different file' : 'Attach from computer'}</div>
        <button style={{ ...rowBtn, justifyContent: 'center', background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#a5b4fc' }} disabled={busy} onClick={() => fileInputRef.current?.click()}>
          <FaUpload /> {busy ? 'Working…' : (attached ? 'Upload a replacement' : 'Upload a file')}
        </button>
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleUpload} />

        <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b7280', letterSpacing: 0.5, margin: '18px 0 8px' }}>{attached ? 'Replace from existing files' : 'Attach from existing files'} ({files.length})</div>
        {files.length === 0 && <div style={{ fontSize: 12, color: '#6b7280', padding: '8px 0' }}>No files uploaded to this case yet.</div>}
        {files.map((f) => (
          <button key={f.id || f.fileId} style={rowBtn} disabled={busy} onClick={() => run(() => onAttachFile(f))}>
            <FaFileAlt style={{ color: '#10b981', flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.originalName || f.fileName}</span>
          </button>
        ))}

        <div style={{ borderTop: '1px solid rgba(75,85,99,0.3)', margin: '18px 0 14px' }} />
        <button style={{ ...rowBtn, justifyContent: 'center', marginBottom: 0, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24' }} disabled={busy} onClick={() => run(onRequest)}>
          <FaPaperPlane /> Request this from the client
        </button>
        <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', marginTop: 6 }}>
          Sends a request into the client's conversation; they attach it and it links here automatically.
        </div>

        {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 12, textAlign: 'center' }}>{error}</div>}
      </div>
    </div>
  );
};

const linkAction = (color) => ({
  background: 'none', border: 'none', color, fontSize: 12, fontWeight: 600,
  cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 5
});

export default RequirementAttachModal;
